import React, { useEffect, useState } from 'react';

function GeneralSettings({ token, selectedProperty }) {
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');
  const [minLength, setMinLength] = useState(10);
  const [maxLength, setMaxLength] = useState(10);
  const [regex, setRegex] = useState('^[0-9]{10}$');
  const [country, setCountry] = useState('');
  const [dialCode, setDialCode] = useState('');
  const [latitude, setLatitude] = useState('');
  const [longitude, setLongitude] = useState('');
  const [locSaving, setLocSaving] = useState(false);
  const [geoSearching, setGeoSearching] = useState(false);
  const [geoQuery, setGeoQuery] = useState('');

  const chainId = String(selectedProperty?.chain_id || '').trim();
  const propertyId = String(selectedProperty?.id || '').trim();
  const headers = (extra = {}) => ({
    Authorization: `Bearer ${token}`,
    'x-chain-id': chainId,
    'x-property-id': propertyId,
    ...extra
  });

  const loadSettings = async () => {
    if (!chainId || !propertyId) return;
    setLoading(true);
    setError('');
    try {
      const res = await fetch('/api/v1/general/settings', { headers: headers() });
      const data = await res.json();
      if (!res.ok) return setError(data?.error || 'Failed to load general settings');
      setMinLength(data.mobile_min_length || 10);
      setMaxLength(data.mobile_max_length || 10);
      setRegex(data.mobile_regex || '^[0-9]{10}$');
      setCountry(data.country_name || '');
      setDialCode(data.dial_code || '');
    } catch {
      setError('Network error while loading general settings');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!chainId || !propertyId) return;
    loadSettings();
  }, [token, chainId, propertyId]);

  useEffect(() => {
    if (selectedProperty) {
      setLatitude(selectedProperty.latitude != null ? String(selectedProperty.latitude) : '');
      setLongitude(selectedProperty.longitude != null ? String(selectedProperty.longitude) : '');
    }
  }, [selectedProperty]);

  const searchNominatim = async (query) => {
    if (!query.trim()) return;
    setGeoSearching(true);
    try {
      const res = await fetch(`https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(query.trim())}&limit=1`, {
        headers: { 'Accept-Language': 'en' }
      });
      const data = await res.json();
      if (data && data.length > 0) {
        setLatitude(data[0].lat);
        setLongitude(data[0].lon);
      } else { setError('No location found for that address'); }
    } catch { setError('Geocoding failed'); }
    finally { setGeoSearching(false); }
  };

  const useMyLocation = () => {
    if (!navigator.geolocation) { setError('Geolocation not supported'); return; }
    navigator.geolocation.getCurrentPosition(
      (pos) => { setLatitude(String(pos.coords.latitude)); setLongitude(String(pos.coords.longitude)); },
      () => setError('Location access denied')
    );
  };

  const saveLocation = async () => {
    if (!chainId || !propertyId) return;
    setLocSaving(true); setError(''); setMessage('');
    try {
      const res = await fetch('/api/v1/core/properties/location', {
        method: 'PUT',
        headers: headers({ 'Content-Type': 'application/json' }),
        body: JSON.stringify({ latitude: latitude ? Number(latitude) : null, longitude: longitude ? Number(longitude) : null })
      });
      const data = await res.json();
      if (!res.ok) return setError(data?.error || 'Failed to save location');
      setMessage('Hotel location updated');
    } catch { setError('Network error'); }
    finally { setLocSaving(false); }
  };

  const save = async () => {
    if (!chainId || !propertyId) return setError('Select a property first from Hotel Config');
    setSaving(true);
    setError('');
    setMessage('');
    try {
      const res = await fetch('/api/v1/general/settings', {
        method: 'POST',
        headers: headers({ 'Content-Type': 'application/json' }),
        body: JSON.stringify({ mobile_min_length: minLength, mobile_max_length: maxLength, mobile_regex: regex })
      });
      const data = await res.json();
      if (!res.ok) return setError(data?.error || 'Failed to save general settings');
      setMessage('General settings updated');
    } catch {
      setError('Network error while saving general settings');
    } finally {
      setSaving(false);
    }
  };

  if (!propertyId) {
    return (
      <div className="page-shell">
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', fontSize: '0.78rem', marginBottom: '0.6rem' }}>
          <span style={{ color: '#6b7280', fontWeight: 500 }}>Hotel Config</span>
          <span style={{ color: '#cbd5e0' }}>›</span>
          <span style={{ color: 'var(--theme-primary-dark)', fontWeight: 600 }}>General Settings</span>
        </div>
        <h2 style={{ color: 'var(--theme-primary-dark)' }}>General Settings</h2>
        <div className="info-banner">
          Please select a property first from <strong>Hotel Config &gt; Select Property</strong>.
        </div>
      </div>
    );
  }

  return (
    <div className="page-shell">
      <div style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', fontSize: '0.78rem', marginBottom: '0.6rem' }}>
        <span style={{ color: '#6b7280', fontWeight: 500 }}>Hotel Config</span>
        <span style={{ color: '#cbd5e0' }}>›</span>
        <span style={{ color: 'var(--theme-primary-dark)', fontWeight: 600 }}>General Settings</span>
      </div>
      <h2 style={{ color: 'var(--theme-primary-dark)' }}>General Settings</h2>
      <p style={{ color: '#6b7280', marginBottom: '1.5rem', fontSize: '0.9rem' }}>
        Configure mobile number validation rules applied during guest registration
      </p>

      {error && <div className="error-banner">{error}</div>}
      {message && <div className="success-banner">{message}</div>}

      <div className="card" style={{ maxWidth: 560 }}>
        <div className="card-body">
          {loading ? (
            <div style={{ textAlign: 'center', padding: '2rem 0', color: '#6b7280', fontSize: '0.9rem' }}>
              Loading settings…
            </div>
          ) : (
            <>
              {/* Property Country — read-only context block */}
              <div style={{ background: '#f7fafc', border: '1px solid var(--theme-border)', borderRadius: '7px', padding: '0.8rem 1rem', marginBottom: '1.5rem', display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: '0.75rem', fontWeight: 600, color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: '0.25rem' }}>
                    Property Country
                  </div>
                  <div style={{ fontWeight: 700, color: '#1f2937', fontSize: '0.95rem' }}>
                    {country || '—'}
                    {dialCode && (
                      <span style={{ fontWeight: 400, color: '#6b7280', marginLeft: 8, fontSize: '0.88rem' }}>
                        ({dialCode})
                      </span>
                    )}
                  </div>
                </div>
                <span className="tag tag-muted" style={{ fontSize: '0.78rem' }}>Read-only</span>
              </div>

              {/* Mobile validation section */}
              <div className="section-head">Mobile Number Validation</div>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem', marginBottom: '0.4rem' }}>
                <div>
                  <label style={{ display: 'block', fontWeight: 600, marginBottom: 4, color: 'var(--theme-text)' }}>
                    Min Length
                  </label>
                  <input
                    type="number"
                    min={1}
                    max={20}
                    value={minLength}
                    onChange={e => setMinLength(Number(e.target.value))}
                    style={{ width: '100%', padding: '8px' }}
                  />
                </div>
                <div>
                  <label style={{ display: 'block', fontWeight: 600, marginBottom: 4, color: 'var(--theme-text)' }}>
                    Max Length
                  </label>
                  <input
                    type="number"
                    min={1}
                    max={20}
                    value={maxLength}
                    onChange={e => setMaxLength(Number(e.target.value))}
                    style={{ width: '100%', padding: '8px' }}
                  />
                </div>
              </div>
              <div style={{ fontSize: '0.82rem', color: '#6b7280', marginBottom: '1.2rem' }}>
                Set both to the same value for fixed-length numbers (e.g. 10 for India).
              </div>

              <div style={{ marginBottom: '0.4rem' }}>
                <label style={{ display: 'block', fontWeight: 600, marginBottom: 4, color: 'var(--theme-text)' }}>
                  Regex Pattern
                </label>
                <input
                  type="text"
                  value={regex}
                  onChange={e => setRegex(e.target.value)}
                  style={{ width: '100%', padding: '8px', fontFamily: 'monospace' }}
                />
              </div>
              <div style={{ fontSize: '0.82rem', color: '#6b7280', marginBottom: '1.5rem' }}>
                Validated against guest mobile at entry.{' '}
                Example:{' '}
                <code style={{ background: 'var(--theme-border-light)', padding: '1px 5px', borderRadius: 3, fontFamily: 'monospace', fontSize: '0.82rem', color: 'var(--theme-text)' }}>
                  {'^[0-9]{10}$'}
                </code>{' '}
                matches exactly 10 digits.
              </div>

              {/* Save footer */}
              <div style={{ display: 'flex', justifyContent: 'flex-end', borderTop: '1px solid var(--theme-border)', paddingTop: '1rem' }}>
                <button
                  onClick={save}
                  disabled={saving}
                  className="btn-success"
                  style={{ padding: '0.55rem 1.5rem', opacity: saving ? 0.7 : 1 }}
                >
                  {saving ? 'Saving…' : 'Save Settings'}
                </button>
              </div>
            </>
          )}
        </div>
      </div>

      {/* Hotel Location Section */}
      <div className="card" style={{ maxWidth: 560, marginTop: '1.5rem' }}>
        <div className="card-body">
          <div className="section-head">Hotel Location</div>
          <p style={{ color: '#6b7280', fontSize: '0.85rem', marginBottom: '1rem' }}>
            Set hotel coordinates via address search, browser location, or manual entry.
          </p>
          <div style={{ display: 'flex', gap: '0.5rem', marginBottom: '1rem' }}>
            <input
              placeholder="Search address..."
              value={geoQuery}
              onChange={(e) => setGeoQuery(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); searchNominatim(geoQuery); } }}
              style={{ flex: 1, padding: '8px' }}
            />
            <button type="button" onClick={() => searchNominatim(geoQuery)} disabled={geoSearching}
              className="btn-primary" style={{ padding: '0.55rem 1rem', opacity: geoSearching ? 0.7 : 1, whiteSpace: 'nowrap' }}>
              {geoSearching ? 'Searching…' : 'Find'}
            </button>
            <button type="button" onClick={useMyLocation}
              className="btn-muted" style={{ padding: '0.55rem 1rem', whiteSpace: 'nowrap' }}>
              Use My Location
            </button>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem', marginBottom: '1rem' }}>
            <div>
              <label style={{ display: 'block', fontWeight: 600, marginBottom: 4, color: 'var(--theme-text)' }}>Latitude</label>
              <input type="text" value={latitude} onChange={(e) => setLatitude(e.target.value)} style={{ width: '100%', padding: '8px' }} placeholder="e.g. 19.0760" />
            </div>
            <div>
              <label style={{ display: 'block', fontWeight: 600, marginBottom: 4, color: 'var(--theme-text)' }}>Longitude</label>
              <input type="text" value={longitude} onChange={(e) => setLongitude(e.target.value)} style={{ width: '100%', padding: '8px' }} placeholder="e.g. 72.8777" />
            </div>
          </div>
          <div style={{ display: 'flex', justifyContent: 'flex-end', borderTop: '1px solid var(--theme-border)', paddingTop: '1rem' }}>
            <button onClick={saveLocation} disabled={locSaving} className="btn-success"
              style={{ padding: '0.55rem 1.5rem', opacity: locSaving ? 0.7 : 1 }}>
              {locSaving ? 'Saving…' : 'Save Location'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default GeneralSettings;
